import { faAngleLeft, faAngleRight, faTruck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { Button, Calendar, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getVesselColourClasses } from '../../helpers/common';
import dayjs from '../../helpers/dayjsSetup';
import { useGlobalFilters } from '../../providers/GlobalFiltersProvider';
import ContainerService from '../../services/Container/ContainerService';

export default function CalendarDashboard({ date, filterStatus, filterSupplier }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(location?.state?.currentDate ? dayjs(location.state.currentDate) : (date ? dayjs(date) : dayjs()));
    const { filters } = useGlobalFilters();

    const visibleRange = useMemo(() => {
        const firstDayOfMonth = currentDate.startOf('month');
        const firstVisible = firstDayOfMonth.startOf('week');
        const lastVisible = firstVisible.add(41, 'day');
        return {
            firstVisible: firstVisible.format('YYYY-MM-DD'),
            lastVisible: lastVisible.format('YYYY-MM-DD')
        };
    }, [currentDate]);

    const { data: calendarContainersData, isLoading, isFetching, error, } = useQuery({
        queryKey: ["calendarContainersData", currentDate.format("YYYY-MM"), visibleRange.firstVisible, visibleRange.lastVisible, filterSupplier, filterStatus, filters],
        queryFn: () => ContainerService.loadCalendarViewContainersData({ currentDate: currentDate.format("YYYY-MM-DD"), firstDate: visibleRange.firstVisible, lastDate: visibleRange.lastVisible, filterSupplier, filterStatus, ...filters })
    });

    const containersData = useMemo(() => {
        if (!calendarContainersData?.data) return {};

        return Object.entries(calendarContainersData.data).reduce((acc, [dateKey, containers]) => {
            acc[dayjs(dateKey).format('YYYY-MM-DD')] = containers;
            return acc;
        }, {});
    }, [calendarContainersData]);

    const prevCount = calendarContainersData?.prevCount ?? 0;
    const nextCount = calendarContainersData?.nextCount ?? 0;

    const onContainerClick = (containerId) => {
        navigate("/shipment-gateway/shipment-details", {
            state: {
                pathname: location.pathname,
                containerID: containerId,
                activeView: 'calendar',
                currentDate: currentDate.format("YYYY-MM-DD"),
                filterSupplier,
                filterStatus,
            },
        });
    };

    const cellRender = (date, info) => {
        const containers = containersData[date.format('YYYY-MM-DD')] || [];
        const isSelected = date.isSame(currentDate, 'day');
        const isWeekend = date.day() === 0 || date.day() === 6;
        const firstDay = date.startOf("month");
        const lastDay = date.endOf("month");
        const isEdgeDay = date.isSame(firstDay, "day") || date.isSame(lastDay, "day");
        return (
            <div
                className={`flex-1 min-h-[128px] grow w-full h-52 overflow-y-auto [scrollbar-gutter:stable] ring-1 ${isSelected ? "ring-cyan-400" : "ring-gray-100"} hover:ring-gray-300 ${isWeekend ? "bg-gray-50" : ""}`}
                onClick={() => setCurrentDate(date)}
            >
                <span className='mr-1 text-sm'>{date.format(isEdgeDay ? "MMM DD" : "DD")}</span>
                <div className='my-1'>
                    {containers.length !== 0 ? (
                        containers.map(container => {
                            const vesselClasses = getVesselColourClasses(container.vesselName);
                            return (
                                <div
                                    key={container.id}
                                    className={`${vesselClasses.bg} p-1 rounded-lg m-1 w-50`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onContainerClick(container.id);
                                    }}
                                >
                                    <div className='bg-white ml-2 px-1 hover:bg-gray-100'>
                                        <div className='flex justify-between'>
                                            <span className='font-semibold text-md'>{container.containerNum}</span>
                                            <span className='text-xs'>{container.containerLoadType === "LCL" ? container.containerLoadType : container.type}</span>
                                        </div>
                                        <div className='flex justify-between text-sm'>
                                            <span className='text-xs'>
                                                {container.statusName}
                                            </span>
                                            {container.deliveryDate != null && (
                                                <FontAwesomeIcon
                                                    icon={faTruck}
                                                    className={`text-xs ${container.isDeliveryConfirmed ? "text-green-500" : "text-red-500"}`}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : null}
                </div>
            </div>
        );
    };

    const weekdays = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
    ];

    return (
        <Spin spinning={isLoading || isFetching} size="large">
            <Calendar
                locale={{
                    "lang": {
                        "locale": "en_US",
                        "placeholder": "Select date",
                        "rangePlaceholder": ["Start date", "End date"],
                        "today": "Today",
                        "now": "Now",
                        "backToToday": "Back to today",
                        "ok": "OK",
                        "clear": "Clear",
                        "month": "Month",
                        "year": "Year",
                        "timeSelect": "Select time",
                        "dateSelect": "Select date",
                        "monthSelect": "Choose a month",
                        "yearSelect": "Choose a year",
                        "decadeSelect": "Choose a decade",
                        "yearFormat": "YYYY",
                        "fieldDateFormat": "M/D/YYYY",
                        "cellDateFormat": "D",
                        "fieldDateTimeFormat": "M/D/YYYY HH:mm:ss",
                        "monthFormat": "MMMM",
                        "fieldWeekFormat": "YYYY-wo",
                        "monthBeforeYear": true,
                        "previousMonth": "Previous month (PageUp)",
                        "nextMonth": "Next month (PageDown)",
                        "previousYear": "Last year (Control + left)",
                        "nextYear": "Next year (Control + right)",
                        "previousDecade": "Last decade",
                        "nextDecade": "Next decade",
                        "previousCentury": "Last century",
                        "nextCentury": "Next century",
                        "shortWeekDays": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                        "shortMonths": [
                            "Jan",
                            "Feb",
                            "Mar",
                            "Apr",
                            "May",
                            "Jun",
                            "Jul",
                            "Aug",
                            "Sep",
                            "Oct",
                            "Nov",
                            "Dec"
                        ]
                    },
                    "timePickerLocale": {
                        "placeholder": "Select time"
                    }
                }}
                value={currentDate}
                onChange={(d) => setCurrentDate(d)}
                onPanelChange={(d) => setCurrentDate(d)}
                className='rounded-3xl px-4 max-h-[200vh] [&_.ant-picker-content_thead]:hidden'
                headerRender={({ value, onChange }) => (
                    <div className='pt-2 -mb-4'>
                        <div className='flex items-center justify-start gap-4 mb-2'>
                            <Button
                                type='text'
                                className='m-0 p-4 rounded-xl border border-gray-200 font-semibold'
                                onClick={() => {
                                    const today = dayjs();
                                    onChange(today);
                                }}
                            >
                                Today
                            </Button>
                            <div className='m-0 p-0 rounded-xl border border-gray-200 ring-1 ring-gray-200 flex items-center'>
                                <Button
                                    className='rounded-none rounded-l-xl m-0 p-0 px-2 border-l-0 border-t-0 border-b-0 border-r-1 bg-gray-100'
                                    onClick={() => {
                                        const newDate = currentDate.subtract(1, 'month');
                                        onChange(newDate);
                                    }}
                                >
                                    <span className="px-1 rounded-md bg-[#01A4A6] font-semibold text-white">
                                        {prevCount}
                                    </span>
                                    <FontAwesomeIcon icon={faAngleLeft} />
                                </Button>
                                <span className='px-4 font-semibold'>
                                    {currentDate.format('MMMM YYYY')}
                                </span>
                                <Button
                                    className='rounded-none rounded-r-xl m-0 p-0 px-2 border-r-0 border-t-0 border-b-0 border-l-1 bg-gray-100'
                                    onClick={() => {
                                        const newDate = currentDate.add(1, 'month');
                                        onChange(newDate);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faAngleRight} />
                                    <span className="px-1 rounded-md bg-[#01A4A6] font-semibold text-white">
                                        {nextCount}
                                    </span>
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-7 text-center font-semibold text-gray-800 mb-2 ">
                            {weekdays.map((day) => (
                                <div key={day}>{day}</div>
                            ))}
                        </div>
                    </div>
                )}
                fullCellRender={cellRender}
            />
        </Spin>
    );
}